/** Default avatar + profile-picture upload helpers. */

const HUES = [140, 96, 168, 120, 84, 152, 108, 132];

/**
 * Default avatar: a Robin Hood character silhouette (feathered cap + bust)
 * on a per-address tinted background. Returned as an SVG data URI.
 */
export function defaultAvatar(address: string): string {
  const seed = address.toLowerCase();
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  const hue = HUES[h % HUES.length];
  const bgA = `hsl(${hue},45%,12%)`;
  const bgB = `hsl(${hue},55%,7%)`;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 96 96" width="96" height="96">
<defs><linearGradient id="b" x1="0" y1="0" x2="0" y2="1">
<stop offset="0%" stop-color="${bgA}"/><stop offset="100%" stop-color="${bgB}"/>
</linearGradient></defs>
<rect width="96" height="96" fill="url(#b)"/>
<g fill="#00c805">
<path d="M30.5 34.5c-1.6-7.4 2.1-14.6 8.9-17.7 1.2-.6 2-.9 3.3-1.2l1.4-.3-.5-1.5c-.5-1.6-.5-1.7.2-2.5.9-1.1 2.3-1.1 3.4 0 .5.5.9 1.3 1.1 2.3l.3 1.6 2.2.4c3.3.6 6.1 2.1 8.6 4.6 1.9 1.9 3 3.6 3.9 6l.5 1.4 4.6-2.4c5.4-2.8 7.9-3.6 10.9-3.6 2.4 0 2.9.5 2 2.1-1.4 2.5-4.6 5.3-8.8 7.7-2.3 1.3-6.9 3.3-9.2 4l-1.6.5v1.4c0 2.7-1.2 6-3.1 8.4-.7.9-.7 1-.3 1.3 3.4 2 5.7 3.6 7.9 5.6 5.3 4.7 8.6 10.6 9.8 17.6.3 1.9.6 5.3.4 5.5-.1.1-13.5.2-29.8.2S18.4 96 18.3 95.8c-.2-.2.1-3.6.4-5.5 1.2-6.9 4.4-12.8 9.6-17.4 2.2-2 4.6-3.7 7.9-5.6l.9-.5-.9-1.1c-2-2.4-3.2-5.6-3.2-8.5v-1l-1.1-.7c-1.5-.9-2.5-2.4-2.9-4.2-.4-1.9.1-3.5 1.3-4.2.9-.5 1.9-.4 2.9.4l.7.5v-3.6c0-3.4 0-3.7-.5-4.2-.9-.9-2.2-3.4-2.9-5.7z"/>
</g></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/**
 * Read a picked image file, downscale it to a square thumbnail and return a
 * data URI small enough to store in a text column (~10–20 KB).
 */
export function fileToAvatarDataUrl(file: File, size = 160): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("that file is not an image"));
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      reject(new Error("image is too large (max 12 MB)"));
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("canvas unavailable"));
        return;
      }
      // center-crop to a square, then draw at target size
      const side = Math.min(img.width, img.height);
      const sx = (img.width - side) / 2;
      const sy = (img.height - side) / 2;
      ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
      try {
        resolve(canvas.toDataURL("image/jpeg", 0.82));
      } catch {
        reject(new Error("could not process that image"));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("could not read that image"));
    };
    img.src = url;
  });
}
